/**
 * Validation AST Nodes
 *
 * Validation defines how student answers are checked:
 * - SimpleValidation: Direct answer matching
 * - (Future) ExpressionValidation: Expression-based validation
 */

import { z } from 'zod';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Simple Validation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Simple validation schema - direct answer matching
 * 
 * Each input has a predetermined correct answer.
 * No complex expressions or calculations.
 * 
 * @example
 * {
 *   type: "SimpleValidation"
 * }
 * 
 * Note: Actual answer values are stored separately (in widget definitions)
 */
export const SimpleValidationSchema = z.object({
  type: z.literal(AST_TYPES.SIMPLE_VALIDATION)
});

export type SimpleValidation = z.infer<typeof SimpleValidationSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Expression Validation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Expression validation schema - validates using a boolean expression
 * 
 * The expression can reference widget values by name.
 * Example: "a + b === 10"
 * 
 * @example
 * {
 *   type: "ExpressionValidation",
 *   expression: "a + b === 10"
 * }
 */
export const ExpressionValidationSchema = z.object({
  type: z.literal(AST_TYPES.EXPRESSION_VALIDATION),
  expression: z.string()
});

export type ExpressionValidation = z.infer<typeof ExpressionValidationSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Mixed Validation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mixed validation schema — combines simple per-widget answers with an expression.
 *
 * Semantics:
 *   - Widgets that declare an `answer` are validated via simple equality.
 *   - Widgets referenced in `expression` but without `answer` are free variables.
 *   - The exercise is correct iff every simple-answer widget matches AND the
 *     boolean expression evaluates true with user inputs in scope.
 *
 * @example
 * {
 *   type: "MixedValidation",
 *   expression: "x + y === a + b"
 * }
 */
export const MixedValidationSchema = z.object({
  type: z.literal(AST_TYPES.MIXED_VALIDATION),
  expression: z.string().min(1)
});

export type MixedValidation = z.infer<typeof MixedValidationSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Union Type
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Union of all validation types
 */
export const ValidationNodeSchema = z.discriminatedUnion("type", [
  SimpleValidationSchema,
  ExpressionValidationSchema,
  MixedValidationSchema
]);

export type ValidationNode = z.infer<typeof ValidationNodeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a simple validation node
 */
export function createSimpleValidation(): SimpleValidation {
  return SimpleValidationSchema.parse({
    type: AST_TYPES.SIMPLE_VALIDATION
  });
}

/**
 * Create an expression validation node
 */
export function createExpressionValidation(expression: string): ExpressionValidation {
  return ExpressionValidationSchema.parse({
    type: AST_TYPES.EXPRESSION_VALIDATION,
    expression
  });
}

/**
 * Create a mixed validation node
 */
export function createMixedValidation(expression: string): MixedValidation {
  return MixedValidationSchema.parse({
    type: AST_TYPES.MIXED_VALIDATION,
    expression
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

export function isSimpleValidation(value: unknown): value is SimpleValidation {
  return SimpleValidationSchema.safeParse(value).success;
}

export function isExpressionValidation(value: unknown): value is ExpressionValidation {
  return ExpressionValidationSchema.safeParse(value).success;
}

export function isMixedValidation(value: unknown): value is MixedValidation {
  return MixedValidationSchema.safeParse(value).success;
}

export function isValidationNode(value: unknown): value is ValidationNode {
  return ValidationNodeSchema.safeParse(value).success;
}

